import { Avatar } from '@/components/design-system'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { nextRunLabel, scheduleLabel } from './format'
import type { OrgCron, WorkerAvatarMap } from './types'

// Só as rotinas com próxima execução entram em "Próximas execuções" (as
// pausadas/sem agenda aparecem no registro "Rotinas"). Recorte curto.
const UPCOMING_LIMIT = 6

interface Props {
  crons?: OrgCron[]
  workerAvatars?: WorkerAvatarMap
}

/**
 * "Próximas execuções" (Iteração 3, Onda A): agregação CROSS-AGENTE dos crons
 * (Dashboard::OrgCrons), ordenada pela próxima execução. Cada linha = quando ·
 * nome da rotina · agente. Deferida (skeleton próprio) — são N chamadas Fleet,
 * não bloqueiam a home. Sem próxima execução em nenhum agente, a seção some.
 */
export function UpcomingRuns({ crons, workerAvatars = {} }: Props) {
  if (!crons) return null

  const upcoming = crons.filter((cron) => cron.next_run_at_ms != null).slice(0, UPCOMING_LIMIT)
  if (upcoming.length === 0) return null

  return (
    <section aria-label="Próximas execuções" className="space-y-2">
      <SectionLabel>Próximas execuções</SectionLabel>
      <ul className="overflow-hidden rounded-xl border border-border bg-card">
        {upcoming.map((cron) => (
          <li
            key={cron.id}
            className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0"
          >
            <span className="w-[92px] flex-none font-mono text-[11px] text-muted-foreground">
              {nextRunLabel(cron)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-foreground">{cron.name}</p>
              <p className="truncate text-[11px] text-faint-foreground">{scheduleLabel(cron)}</p>
            </div>
            <div className="flex flex-none items-center gap-1.5">
              <span className="text-[12px] text-muted-foreground">{cron.worker_name}</span>
              <Avatar src={workerAvatars[cron.worker_id] ?? undefined} alt="" name={cron.worker_name} hueSeed={cron.worker_id} size="xxs" className="flex-none" />
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Skeleton com a forma da lista (3 linhas) enquanto os crons chegam. */
export function UpcomingRunsSkeleton() {
  return (
    <section aria-label="Próximas execuções" className="space-y-2">
      <SectionLabel>Próximas execuções</SectionLabel>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0">
            <div className="h-3 w-[72px] flex-none animate-pulse rounded bg-muted" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="h-3 w-2/5 animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-5 w-5 flex-none animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </section>
  )
}
